import { useState, useRef, useEffect, useCallback } from 'react';
import transformChargerAvailability from '../../../api/transformers/transformChargerAvailability';
import { useCachedFetch } from '../../../context/apiCacheProvider';
import { TomTomConnectors } from '../../../types';

const CHARGER_URL = 'https://api.tomtom.com/search/2/chargingAvailability.json';

export default function useChargerAvailability(id: string) {
  const { fetchRecords } = useCachedFetch();
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState<TomTomConnectors | null>(null);
  const [error, setError] = useState('');

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchChargingStation = useCallback(
    async (id: string) => {
      if (!id) return;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      try {
        abortControllerRef.current = new window.AbortController();
        setLoading(true);

        const json = await fetchRecords<TomTomConnectors>(CHARGER_URL, {
          key: process.env.REACT_APP_TOMTOM_API_KEY || '',
          chargingAvailability: id,
        });

        abortControllerRef.current = null;

        setLoading(false);

        if (json) {
          setAvailability(transformChargerAvailability(json));
        }
      } catch (error: any) {
        setLoading(false);

        if (error.name !== 'AbortError') {
          setError(error);
        }
        abortControllerRef.current = null;
      }
    },
    [fetchRecords],
  );

  useEffect(() => {
    fetchChargingStation(id);
  }, [fetchChargingStation, id]);

  return {
    error,
    availability,
    loading,
  };
}
